import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardHeader,
  CardBody,
  Heading,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiRefreshCw,
  FiRotateCcw,
} from "react-icons/fi";
import { adminApiService } from "../services/adminApiService";
import { Category, QueryParams } from "../types";

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  // Fetch categories data
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: QueryParams = {
        page: pagination.page,
        per_page: pagination.per_page,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await adminApiService.getCategories(params);
      setCategories(response.data);
      setPagination({
        page: response.page,
        per_page: response.per_page,
        total: response.total,
        total_pages: response.total_pages,
      });
    } catch (err: any) {
      setError(err.message || "Gagal memuat data kategori");
      toast({
        title: "Error",
        description: "Gagal memuat data kategori",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Create or update category
  const handleSave = async () => {
    try {
      if (isEditing && selectedCategory) {
        await adminApiService.updateCategory(selectedCategory.id, formData);
        toast({
          title: "Berhasil",
          description: "Kategori berhasil diperbarui",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await adminApiService.createCategory(formData);
        toast({
          title: "Berhasil",
          description: "Kategori berhasil ditambahkan",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      fetchCategories();
      handleCloseModal();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Gagal menyimpan kategori",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Delete category
  const handleDelete = async () => {
    if (!selectedCategory) return;

    try {
      await adminApiService.deleteCategory(selectedCategory.id);
      toast({
        title: "Berhasil",
        description: "Kategori berhasil dihapus",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchCategories();
      onDeleteClose();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Gagal menghapus kategori",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Restore category
  const handleRestore = async (id: number) => {
    try {
      await adminApiService.restoreCategory(id);
      toast({
        title: "Berhasil",
        description: "Kategori berhasil dipulihkan",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchCategories();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Gagal memulihkan kategori",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle modal open for create/edit
  const handleOpenModal = (category?: Category) => {
    if (category) {
      setSelectedCategory(category);
      setIsEditing(true);
      setFormData({
        name: category.name,
        code: category.code || "",
      });
    } else {
      setSelectedCategory(null);
      setIsEditing(false);
      setFormData({
        name: "",
        code: "",
      });
    }
    onOpen();
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedCategory(null);
    setIsEditing(false);
    setFormData({
      name: "",
      code: "",
    });
    onClose();
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Handle search
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchCategories();
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    fetchCategories();
  }, [pagination.page, pagination.per_page]);

  if (loading && categories.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <VStack>
          <Spinner size="xl" />
          <Text>Memuat data kategori...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Card>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="lg">Manajemen Kategori</Heading>
            <HStack>
              <Button
                leftIcon={<FiRefreshCw />}
                variant="outline"
                onClick={fetchCategories}
                isLoading={loading}
              >
                Refresh
              </Button>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                onClick={() => handleOpenModal()}
              >
                Tambah Kategori
              </Button>
            </HStack>
          </Flex>
        </CardHeader>

        <CardBody>
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
          )}

          {/* Filters */}
          <VStack spacing={4} align="stretch" mb={6}>
            <HStack spacing={4}>
              <InputGroup flex={1}>
                <InputLeftElement>
                  <FiSearch />
                </InputLeftElement>
                <Input
                  placeholder="Cari kategori..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
              </InputGroup>
              <Button onClick={handleSearch} isLoading={loading}>
                Cari
              </Button>
              <Button variant="outline" onClick={resetFilters}>
                Reset
              </Button>
            </HStack>
          </VStack>

          {/* Table */}
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>Nama</Th>
                  <Th>Kode</Th>
                  <Th>Status</Th>
                  <Th>Aksi</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categories.map((category) => (
                  <Tr key={category.id}>
                    <Td>{category.id}</Td>
                    <Td fontWeight="medium">{category.name}</Td>
                    <Td>{category.code || "-"}</Td>
                    <Td>
                      <Badge colorScheme={category.is_disabled ? "red" : "green"}>
                        {category.is_disabled ? "Nonaktif" : "Aktif"}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Lihat detail"
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenModal(category)}
                        />
                        <IconButton
                          aria-label="Edit"
                          icon={<FiEdit />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleOpenModal(category)}
                        />
                        {category.is_disabled ? (
                          <IconButton
                            aria-label="Pulihkan"
                            icon={<FiRotateCcw />}
                            size="sm"
                            variant="ghost"
                            colorScheme="green"
                            onClick={() => handleRestore(category.id)}
                          />
                        ) : (
                          <IconButton
                            aria-label="Hapus"
                            icon={<FiTrash2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => {
                              setSelectedCategory(category);
                              onDeleteOpen();
                            }}
                          />
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <HStack justify="center" mt={6}>
              <Button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                variant="outline"
              >
                Sebelumnya
              </Button>
              <Text>
                Halaman {pagination.page} dari {pagination.total_pages}
              </Text>
              <Button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
                variant="outline"
              >
                Selanjutnya
              </Button>
            </HStack>
          )}
        </CardBody>
      </Card>

      {/* Category Form Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Kategori" : "Tambah Kategori"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Nama Kategori</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Masukkan nama kategori"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Kode Kategori</FormLabel>
                <Input
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                  placeholder="Masukkan kode kategori"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              Batal
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isDisabled={!formData.name.trim()}
            >
              {isEditing ? "Perbarui" : "Simpan"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Hapus Kategori</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Apakah Anda yakin ingin menghapus kategori{" "}
              <strong>{selectedCategory?.name}</strong>? Kategori akan di-soft delete dan dapat dipulihkan kembali.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Batal
            </Button>
            <Button colorScheme="red" onClick={handleDelete}>
              Hapus
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CategoryManagement;
